import { useAwsIp } from "../../../context/Dashboard/aws/contextIp.js";
import { useDesign } from "../../../context/contextDesign.js";

const ScanCard = () => {
  const {
    ipScanLoading,
    ipDashboardView,
    handleSubmit,
    setIp,
    ip,
    error
  } = useAwsIp();
  const { toggleUpgradeCard } = useDesign();

  const ipExamples = [
    { label: "Public IPv4", value: "54.123.45.67" },
    { label: "Elastic IP", value: "3.210.15.44" },
    { label: "Instance DNS", value: "ec2-54-123-45-67.compute-1.amazonaws.com" }
  ];

  return (
    <div className="scan-card d-flex justify-content-center align-items-center">
      <div className="card shadow-sm border-1 rounded-4">
        <div className="d-flex flex-row align-items-center h-100">

          {/* 🔹 Left: Form Section */}
          <div className="scan-form-section p-3 w-50">
            <form
              onSubmit={handleSubmit}
              className="d-flex flex-column justify-content-center align-items-center"
            >
              <div className="fs-1 fw-bold text-center heading mb-3">
                Scan EC2 IP
              </div>

              <div
                className="fs-6 text-center fw-semibold mb-5 subheading text-muted"
                style={{ width: 280 }}>
                Enter the ec2 ip below to scan for potential vulnerabilities.
              </div>

              <div
                className="mb-2 w-100 input-group rounded-2"
                style={{ height: 43 }}
              >
                <input
                  type="text"
                  placeholder="Enter EC2 IP"
                  className="p-2 text-center form-control"
                  onChange={(e) => setIp(e.target.value)}
                  disabled={ipScanLoading || ipDashboardView}
                />
              </div>

              <div style={{ width: 260 }}>
                {error && (
                  <div
                    className="error-hover text-danger text-center fw-semibold mb-2"
                    onClick={error.card ? toggleUpgradeCard : undefined}
                    style={{ cursor: error.card ? "pointer" : "default" }}
                  >
                    {error.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 fw-semibold fs-6"
                disabled={ipScanLoading || ipDashboardView || !ip}
                style={{ height: 46 }}
              >
                Submit
              </button>
            </form>
          </div>

          {/* 🔹 Right: Documentation Section */}
          <div className="scan-video-section h-100 w-50 p-4 bg-light d-flex flex-column border-start overflow-auto">
            {/* Documentation Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-cpu-fill text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">IP DOCUMENTATION</span>
              </div>
              <p className="text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                An EC2 instance is a virtual server running in the AWS cloud. To scan correctly, ensure your IP is a public IP address.
              </p>
            </div>

            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-shield-lock-fill text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">EC2 DEPLOYMENT GUIDE</span>
              </div>
              <p className="text-muted" style={{ fontSize: '12.5px', lineHeight: '1.5' }}>
                Follow these steps to ensure your instance is correctly configured for external security assessment.
              </p>
            </div>

            {/* Full Process Steps */}
            <div className="d-flex flex-column gap-4 mb-4">

              {/* Step 1: Instance State */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 1</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Instance Deployment</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Log in to <strong>AWS Console</strong>, navigate to <strong>EC2</strong>, and click <strong>Launch Instance</strong>.</>,
                    <>Ensure the instance is in a <strong>Running</strong> state before attempting a scan.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 2: Network Security */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 2</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Security Groups</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Check your <strong>Inbound Rules</strong> to ensure necessary ports (e.g., 80, 443, 22) are open.</>,
                    <>Scanning may fail if the instance is behind a strict <strong>Network ACL</strong> or Firewall.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 3: IP Retrieval */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 3</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Identify Public IP</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Select your instance and look for the <strong>Public IPv4 address</strong> in the details tab.</>,
                    <>Alternatively, use the <strong>Public IPv4 DNS</strong> name for the scan input.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Supported Examples Footer */}
            <div className="mt-auto pt-3 border-top">
              <div className="mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle-fill text-success"></i>
                <span className="fw-bold small text-secondary">VALID INPUT FORMATS</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {ipExamples.map((ex, i) => (
                  <div key={i} className="p-2 rounded bg-white border border-light-subtle shadow-sm">
                    <div className="text-primary fw-bold mb-1" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{ex.label}</div>
                    <code className="text-dark-emphasis d-block text-truncate p-1 bg-light rounded" style={{ fontSize: '11px' }}>
                      {ex.value}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanCard;